"use client";

import { memo } from "react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { PhoneInput } from "@/components/ui/phone-input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, LogIn, HeartHandshake } from "lucide-react";
import Image from "next/image";
import { PaymentMethodsStrip } from "@/components/ui/payment-methods-strip";
import type { UseFormReturn } from "react-hook-form";
import type { Value } from "react-phone-number-input";

export interface DonationPhoneStepProps {
  form: UseFormReturn<{ mobile: Value }>;
  cartMode: boolean;
  onLogin: () => void;
  onFastDonation: () => void;
  onQuickDonation?: () => void;
  isCheckingPhone: boolean;
  isPreparingPayment: boolean;
  isQuickDonating?: boolean;
  error: string | null;
  t: (key: string) => string;
  /** When true, renders the simple phone-only layout regardless of cartMode. */
  phoneOnlyLayout?: boolean;
  /** Hidden for deduction checkouts, which the online gateway must handle. */
  showBankTransferHint?: boolean;
  cartSubmitLabel?: string;
}

function DonationPhoneStepComponent({
  form,
  cartMode,
  onLogin,
  onFastDonation,
  onQuickDonation,
  isCheckingPhone,
  isPreparingPayment,
  isQuickDonating = false,
  error,
  t,
  phoneOnlyLayout = false,
  showBankTransferHint = true,
  cartSubmitLabel,
}: DonationPhoneStepProps) {
  const busy = isCheckingPhone || isPreparingPayment || isQuickDonating;
  const useSimpleLayout = cartMode || phoneOnlyLayout;
  /** Apple Pay guest phone step: fast donation only (no guest continue). */
  const applePayPhoneOnly = phoneOnlyLayout;

  return (
    <div className="space-y-1">
      {useSimpleLayout ? (
        <>
          <p className="text-sm font-medium text-gray-500 mb-2">
            {t("common.mobile")}
          </p>
          <Form {...form}>
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (applePayPhoneOnly && onQuickDonation) {
                  onQuickDonation();
                } else {
                  onLogin();
                }
              }}
            >
              <FormField
                control={form.control}
                name="mobile"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormControl>
                      <PhoneInput
                        placeholder="5XXXXXXXXX"
                        value={field.value}
                        onChange={field.onChange}
                        disabled={busy}
                        defaultCountry="SA"
                        className="h-14 text-base rounded-xl border -4 focus-visible:ring-2 focus-visible:ring-primary/20"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <div className={applePayPhoneOnly ? undefined : "flex gap-3"}>
                {!applePayPhoneOnly && (
                  <Button
                    type="submit"
                    disabled={busy}
                    className="flex-1 h-14 rounded-xl font-bold bg-primary hover:bg-primary/90 text-white"
                  >
                    {isCheckingPhone ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <>
                        <LogIn className="h-5 w-5 me-2" />
                        {cartSubmitLabel || t("auth.login")}
                      </>
                    )}
                  </Button>
                )}
                {onQuickDonation && (
                  <Button
                    type={applePayPhoneOnly ? "submit" : "button"}
                    onClick={applePayPhoneOnly ? undefined : onQuickDonation}
                    disabled={busy}
                    className={
                      applePayPhoneOnly
                        ? "w-full h-14 rounded-xl font-bold bg-success hover:bg-success/90 text-white"
                        : "flex-1 h-14 rounded-xl font-bold bg-success hover:bg-success/90 text-white"
                    }
                  >
                    {isQuickDonating ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <>
                        <HeartHandshake className="h-5 w-5 me-2" />
                        {t("checkout.fastDonation")}
                      </>
                    )}
                  </Button>
                )}
              </div>
            </form>
          </Form>
        </>
      ) : (
        <>
          <Form {...form}>
            <form
              className="space-y-2 mt-4"
              onSubmit={(e) => {
                e.preventDefault();
                onFastDonation();
              }}
            >
              <FormField
                control={form.control}
                name="mobile"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <label className="block text-sm font-medium text-gray-500">
                      {t("common.mobile")}
                    </label>
                    <div className="flex gap-2">
                      <FormControl className="flex-1 min-w-0">
                        <PhoneInput
                          placeholder="0000 0000 00"
                          value={field.value}
                          onChange={field.onChange}
                          disabled={busy}
                          defaultCountry="SA"
                          className="h-14 text-base rounded-xl bg-background focus-visible:ring-2 focus-visible:ring-primary/20"
                        />
                      </FormControl>
                      <Button
                        type="submit"
                        disabled={busy}
                        className="shrink-0 h-14 rounded-xl font-bold bg-success hover:bg-success/90 text-white text-sm px-6"
                      >
                        {isPreparingPayment ? (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                          t("common.donateNow")
                        )}
                      </Button>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </Form>
          <div className="text-center pt-1">
            <button
              type="button"
              onClick={onLogin}
              disabled={busy}
              className="text-sm text-gray-500 hover:text-primary transition-colors"
            >
              {isCheckingPhone && (
                <Loader2 className="h-3 w-3 animate-spin inline me-1" />
              )}
              {t("auth.haveAccountLogin")}
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-5 pt-4 border-gray-100">
            <span className="text-sm font-medium text-gray-700">
              {t("checkout.donateVia")}
            </span>
            <PaymentMethodsStrip
              items={["mada", "visa", "mastercard", "apple-pay"]}
              size="sm"
              className="justify-center gap-5"
            />
            {showBankTransferHint && (
              <>
                <span className="text-sm text-gray-500">
                  {t("checkout.orShort")}
                </span>
                <span className="inline-flex items-center gap-1 text-sm font-bold ">
                  <Image
                    src="/icons/bank.svg"
                    alt="Bank Transfer"
                    width={20}
                    height={20}
                  />
                  {t("checkout.bankTransferLabel")}
                </span>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export const DonationPhoneStep = memo(DonationPhoneStepComponent);
